import { clsx } from 'clsx';
import type { CSSProperties, ReactNode } from 'react';

/**
 * Card — the kit's `.card` / `.card-*`, and the small pieces that sit in one.
 *
 * The variants carry meaning rather than decoration, which is why they are
 * named for the state and not the colour:
 *   accent  a card the eye should land on first (brand-300 border)
 *   warn    something needs attention (warning border)
 *   danger  a red ROW — a critical vital, a reaction (§Doctor 03 [5], [6])
 *   panic   a red BANNER — filled. Reserved for a value that cannot wait an
 *           hour (§Doctor 03 [8]). Outlined vs filled is what keeps the two
 *           apart, so do not reach for `panic` to mean "quite important".
 *   ghost   a placeholder or an add-slot (dashed, transparent)
 *   flat    grouped content with no edge of its own (surface-100, no border)
 */

export type CardKind = 'default' | 'accent' | 'warn' | 'danger' | 'panic' | 'ghost' | 'flat';

const KIND_CLASS: Record<CardKind, string> = {
  default: '',
  accent: 'card-acc',
  warn: 'card-warn',
  danger: 'card-danger',
  panic: 'card-panic',
  ghost: 'card-ghost',
  flat: 'card-flat',
};

interface CardProps {
  children: ReactNode;
  kind?: CardKind;
  onClick?: () => void;
  className?: string;
  /**
   * Escape hatch for the per-instance overrides the canvases apply inline —
   * e.g. the Doctor home's now-serving card is `card card-acc` on a --brand-100
   * fill. Use a token, never a literal, or the card stops following the theme.
   */
  style?: CSSProperties;
}

export function Card({ children, kind = 'default', onClick, className, style }: CardProps) {
  const classes = clsx('card', KIND_CLASS[kind], className);

  if (!onClick) {
    return <div className={classes} style={style}>{children}</div>;
  }

  // A whole card acting as one control. Rendered as a button so it is reachable
  // by keyboard and announced as actionable, rather than a div with a handler.
  return (
    <button type="button" className={clsx(classes, 'text-start w-full')} style={style} onClick={onClick}>
      {children}
    </button>
  );
}

/**
 * Avatar — `.av` / `.av-sm` / `.av-lg`. Initials on a brand wash.
 *
 * Lives here because in the canvases it never appears outside a card or a row.
 */
export function Avatar({
  initials,
  size = 'md',
  className,
}: {
  initials: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}) {
  return (
    <span
      className={clsx('av', size === 'sm' && 'av-sm', size === 'lg' && 'av-lg', className)}
      aria-hidden="true"
    >
      {initials}
    </span>
  );
}

/**
 * EmptyState — deskkit.emptystate.
 *
 * The action of a zero state is often the only control on the board, so it gets
 * a real control's size (a DChip) rather than a label-sized chip. That was an
 * owner review decision on 9 Sep, recorded in PROGRESS LOG — worth preserving
 * rather than "tidying" to a smaller chip later.
 */
export function EmptyState({
  title,
  sub,
  action,
  className,
}: {
  title: string;
  sub?: string;
  action?: { label: string; onClick: () => void };
  className?: string;
}) {
  return (
    <div className={clsx('emptystate', className)}>
      <p className="es-t">{title}</p>
      {sub ? <p className="t-sec mut">{sub}</p> : null}
      {action ? (
        <p className="mt12">
          <button type="button" className="dchip dchip-outline" onClick={action.onClick}>
            {action.label}
          </button>
        </p>
      ) : null}
    </div>
  );
}

/**
 * Crumb — deskkit.crumb: `‹ Patients ▸ Ahmed H.`
 *
 * The desk has NO global back button (§0.6); this is how a drill-in says where
 * it came from. Law 3 governs the other direction: siblings a user hops between
 * carry a tab switcher instead, and only drill-downs use a crumb.
 */
export function Crumb({ parts, className }: { parts: ReactNode[]; className?: string }) {
  return (
    <p className={clsx('t-sec', 'mut', className)}>
      {parts.map((part, i) => (
        <span key={i}>
          {i > 0 ? <span className="mut"> ▸ </span> : null}
          {part}
        </span>
      ))}
    </p>
  );
}
